import type { RegistrationIntakeModelView } from '@actschurch/ccms-sdk';
import { cleanText } from './rules';
import { splitName } from './casePeople';
import {
  buildPerson, emptyPerson, type Candidates, type PersonResult, type RecordResult,
} from './people';

type IntakeParticipant = NonNullable<RegistrationIntakeModelView['participants']>[number];

/** What we read from `registrationService.getView(id).participants[]`. */
export interface RegistrationParticipantLike {
  memberID?: number | null;
  name?: string | null;
  surname?: string | null;
  fullName?: string | null;
  email?: string | null;
  phone?: string | null;
  gender?: string | null;
  dateOfBirth?: string | null;
  isMain?: boolean;
  isApplicant?: boolean;
}

/** What we read from `registrationService.getView(id)`. */
export interface RegistrationViewLike {
  participants?: RegistrationParticipantLike[] | null;
  /** Null on registrations that predate `Registration.intakeID`. */
  intakeID?: number | null;
  locationName?: string | null;
}

/** A `MemberRegistration` junction row from `registrationService.getMembers(id)`. */
export interface RegistrationLinkLike {
  memberID: number;
  isMain?: boolean;
  isApplicant?: boolean;
}

/** Source 2 for a registration: its members, the view's details, and its intake when it has one. */
export interface RegistrationDetail {
  view: RegistrationViewLike;
  links: RegistrationLinkLike[];
  intake: RegistrationIntakeModelView | null;
}

const norm = (v: unknown) => String(v ?? '').toLowerCase().replace(/\s+/g, ' ').trim();

interface Attendee {
  link: RegistrationLinkLike;
  /** The view's detail row for this member, when there is one. */
  part: RegistrationParticipantLike | null;
}

/** Which intake participant is this member? By member id, then exact name, then "the only one". */
function intakeParticipantFor(
  a: Attendee, intake: RegistrationIntakeModelView | null, count: number,
): IntakeParticipant | null {
  const list = intake?.participants ?? [];
  if (list.length === 0) return null;
  const name = norm(a.part?.fullName ?? `${a.part?.name ?? ''} ${a.part?.surname ?? ''}`);
  return (
    list.find((p) => p.memberID != null && p.memberID === a.link.memberID)
    ?? (name !== '' ? list.find((p) => norm(`${p.name ?? ''} ${p.surname ?? ''}`) === name) : undefined)
    ?? (list.length === 1 && count === 1 ? list[0] : null)
  ) ?? null;
}

/**
 * Who is attending the course, and where each of their values lives.
 *
 * ⚠️ **Membership is the junction rows (`getMembers`), not the view's participants.** The two can
 * disagree: a member linked without a matching detail row is in the former and missing from the
 * latter (the Members tab documents the same trap). Those people are kept, with no details of their
 * own, and Source 3 fills them by member id.
 *
 *  • Attendees = the members, minus the applicant when the intake says the sign-up was made on someone
 *    else's behalf (the applicant is then only the contact). A registration with no intake has no
 *    applicant to cut, so nobody is dropped.
 *  • A = the member marked main (else the first), B = the other attendee.
 *  • Order per field: the view's participant → the matching intake participant → the applicant's own
 *    details (only for the applicant, on a self sign-up).
 *  • There is no custom date-of-birth answer on registrations, so a missing date of birth waits for
 *    Source 3.
 *  • Every attendee has a `memberID`, so Source 3 uses the safe by-id route.
 *
 * Location is empty on a remote course (the sample is `isRemote: true`), so it is never "missing".
 */
export function resolveRegistration(
  detail: RegistrationDetail,
  ctx: { id: number; reference: string; listLocation: string | null },
): RecordResult {
  const { view, links, intake } = detail;
  const parts = view.participants ?? [];
  const byMember = new Map(
    parts.filter((p) => p.memberID != null).map((p) => [p.memberID as number, p]),
  );

  // Membership wins; only if the junction list is empty do we trust the view's own participants.
  const roster: Attendee[] = links.length > 0
    ? links.map((link) => ({ link, part: byMember.get(link.memberID) ?? null }))
    : parts
      .filter((p) => p.memberID != null)
      .map((p) => ({
        link: { memberID: p.memberID as number, isMain: p.isMain, isApplicant: p.isApplicant },
        part: p,
      }));

  const onBehalf = intake?.isForSelf === false;
  const attendees = roster.filter((a) => !(onBehalf && a.link.isApplicant && !a.link.isMain));
  const main = attendees.find((a) => a.link.isMain) ?? attendees[0] ?? null;
  const other = main ? attendees.find((a) => a !== main) ?? null : null;

  const personFor = (a: Attendee): PersonResult => {
    const ip = intakeParticipantFor(a, intake, attendees.length);
    const applicant = intake && !onBehalf
      && (a.link.isApplicant || intake.matchedMemberID === a.link.memberID)
      ? intake : null;
    const [first, last] = splitName(a.part?.fullName);

    const cand: Candidates = {
      firstName: [a.part?.name, ip?.name, applicant?.firstName, first],
      surname: [a.part?.surname, ip?.surname, applicant?.surname, last],
      email: [a.part?.email, ip?.email, applicant?.email],
      phone: [a.part?.phone, ip?.phone, applicant?.phone, applicant?.altContact],
      gender: [a.part?.gender, ip?.gender, applicant?.gender],
      dateOfBirth: [a.part?.dateOfBirth, ip?.dateOfBirth],
    };
    return buildPerson(cand, a.link.memberID ?? null);
  };

  return {
    id: ctx.id,
    reference: ctx.reference,
    location: cleanText(ctx.listLocation) ?? cleanText(view.locationName),
    a: main ? personFor(main) : emptyPerson(),
    b: other ? personFor(other) : null,
    extraPeople: Math.max(0, attendees.length - 2),
    failed: false,
    requireLocation: false,
  };
}
